import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
// Cloud React components
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Button, ToggleSwitch } from 'Components';
import DirectionsMap from 'Components/Map/components/DirectionsMap';
// Material UI
import { Grid, Icon, Typography } from '@mui/material';
// Styles
import '../nearest.scss';
// Actions
import {
  addPointers,
  drawGetDirections,
  setMapRoutesOptions,
  openNearestDirectionsModal,
  clearRedux,
  setToggleFullScreen,
  printView
} from 'Redux/actions';

const DirectionsModal = (props) => {
  const dispatch = useDispatch();
  const { intl, onClose, mapRef, resetSelectedItems } = props;
  const { messages } = intl;

  /*********************************************************
   * Get the redux properties
  *********************************************************/
  const {
    openDirectionsModal: open,
    directionsModalData: points
  } = useSelector(state => state.realtimeMapFindNearestRedux);

   const {
    generatePrint
  } = useSelector((state) => state.realtimeMapRedux);

  /*********************************************************
   * States
  *********************************************************/
  const [directionsConfig, setDirectionsConfig] = useState({ avoidTolls: false, optimizeWaypoints: true, avoidHighways: false });

  /*********************************************************
   * Use Effect
  *********************************************************/
  useEffect(() => {
    if (points && open) {
      dispatch(addPointers([]));
      dispatch(clearRedux("REALTIMEMAPS", { directions: { options: {}, getDirections: false} }))

      if (window.clearRoutes) {
        clearTimeout(window.clearRoutes)
      }

      window.clearRoutes = setTimeout(() => {
        dispatch(addPointers(points));
        dispatch(drawGetDirections(true));
        dispatch(setMapRoutesOptions(directionsConfig));

        window.clearRoutes = null;
      }, 300);
    }
  }, [directionsConfig, points, open]);

  useEffect(()=>{
    if(generatePrint){
        window.print();
        setTimeout(()=> {
            dispatch(printView(false))
            dispatch(setToggleFullScreen(false));
            mapRef.current?.setZoomOut(-1);
            document.getElementById("id_sidePanel").classList.remove('hide-elementToPrint');
            document.querySelector('body').classList.remove('print-app-container');
            document.querySelector('html').classList.remove('print-app-container');
            document.getElementById('root').classList.remove('print-app-container');        
            document.querySelector('#root').classList.remove('print-app-container');
            document.getElementById("id_appContainer").classList.remove('print-app-container');

            //Remove print panel
            const panelToPrint = document.getElementById('directionsPanelToPrint');
            if (panelToPrint) {
                panelToPrint.innerHTML = '';
            }
            
        },500);
    }
  },[generatePrint])

  /********************************************************
  * Close the window component
  *********************************************************/
  const handleClose = useCallback(() => {
    dispatch(addPointers([]));
    dispatch(openNearestDirectionsModal(false, null));
    dispatch(drawGetDirections(false));
    resetSelectedItems && resetSelectedItems()
    //if (onClose) onClose();
  });

  /********************************************************
  * Print the map in options
  *********************************************************/
  const onPrint = () => {
    mapRef.current?.setZoomOut(1);
    dispatch(setToggleFullScreen(true));

    const panelContainer = document.getElementById('directionsPanel');
    const panelToPrint = document.getElementById('directionsPanelToPrint');
    if (panelContainer && panelToPrint) {
        panelToPrint.replaceChildren(...panelContainer.cloneNode(true).childNodes);
    }

    setTimeout(()=> {
        document.getElementById("id_sidePanel").classList.add('hide-elementToPrint');
        document.querySelector('body').classList.add('print-app-container');
        document.querySelector('html').classList.add('print-app-container');
        document.getElementById('root').classList.add('print-app-container');        
        document.querySelector('#root').classList.add('print-app-container');
        document.getElementById("id_appContainer").classList.add('print-app-container');
    }, 500);

    setTimeout(()=>{
        dispatch(printView(true));
    },1000);
  }

  return (
    <Modal
      title={messages['nearest_vehicleDirections']}
      open={open}
      position="right"
      size="small"
      handleClose={handleClose}
      className="nearest-directions-container"
      headerStyle={{ top: 0 }}
    >
      <ModalBody>
        <div style={{ padding: 25 }}>
          {
            (points && points?.length > 1) &&
            <div style={{ padding: '0px 0px 10px 0px' }}>
              <Grid
                container
                wrap="nowrap"
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item>
                  <Icon className="nearest-start-icon">
                    room
                  </Icon>
                </Grid>
                <Grid item xs zeroMinWidth>
                  <Typography noWrap variant="subtitle2" color="textSecondary">
                    {points[0].name || messages['notAvailable']}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                wrap="nowrap"
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item>
                  <Icon className="nearest-end-icon">
                    room
                  </Icon>
                </Grid>
                <Grid item xs zeroMinWidth>
                  <Typography noWrap variant="subtitle2" color="textSecondary">
                    {points[1].name || messages['notAvailable']}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          }

          <div style={{ padding: '10px 0px 10px 0px' }}>
            <ToggleSwitch
              id="optimizeWaypoints"
              name="optimizeWaypoints"
              medium
              blueToggle
              optionLabels={[messages['yes'], messages['no']]}
              checked={directionsConfig.optimizeWaypoints}
              onChange={(value) => {
                setDirectionsConfig({ ...directionsConfig, optimizeWaypoints: value });
              }}
            />
            <label className={'labelMapSettings'}>
              {messages['nearest_optimizeRoute']}
            </label>
          </div>

          <div style={{ padding: '10px 0px' }}>
            <ToggleSwitch
              id="avoidTolls"
              name="avoidTolls"
              medium
              blueToggle
              optionLabels={[messages['yes'], messages['no']]}
              checked={directionsConfig.avoidTolls}
              onChange={(value) => {
                setDirectionsConfig({ ...directionsConfig, avoidTolls: value });
              }}
            />
            <label className={'labelMapSettings'}>
              {messages['nearest_avoidTolls']}
            </label>
          </div>

          <div style={{ padding: '10px 0px' }}>
            <ToggleSwitch
              id="avoidHighways"
              name="avoidHighways"
              medium
              blueToggle
              optionLabels={[messages['yes'], messages['no']]}
              checked={directionsConfig.avoidHighways}
              onChange={(value) => {
                setDirectionsConfig({ ...directionsConfig, avoidHighways: value });
              }}
            />
            <label className={'labelMapSettings'}>
              {messages['nearest_avoidHighways']}
            </label>
          </div>

          <div style={{ height: '100%', overflow: 'auto' }}>
            <DirectionsMap messages={messages} />
          </div>
        </div>
      </ModalBody>

      <ModalFooter style={{ bottom: 50 }}>
        <Button
          disabled={false}
          onClick={handleClose}
          className="btn-cancel"
        >
          {messages['close']}
        </Button>
        <Button
            id={'btn-print'}
            type="button"
            onClick={() => {
                onPrint();
            }}
            className="btn-green"
            //disabled={pointers?.length < 2}
        >
            {messages['print']}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(DirectionsModal);
